import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import isEmpty from 'lodash/isEmpty';
import { useLocation, useParams, useSearchParams } from 'react-router-dom';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useAppDispatch, useAppSelector } from '../../../../app/hooks';
import { setModulePollingList, setRefreshReportPreselectedModules, setTaskTrackerMap } from '../../../../store/investor/action';
import { unlockOrRefreshReport } from '../../../../store/investor/investorApiHelper';
import { isManagementProfileFetchInFlight, isManagementProfileLocked } from '../../../../store/investor/taskTrackerResolution';
import { useTaskTrackerResolution } from '../../../../common/hooks/useTaskTrackerResolution';
import {
    TAB_TO_TASK_TRACKER_MAP,
    TAB_LOCK_IDS_AFTER_DIRECTORS_OVERVIEW,
    CROSS_MODULE_TASK_STEPS,
    TabModule,
} from '../../../../utils/constants/commonConstants';

import clsx from 'clsx';
import { Button, CheckBox } from '../../../../common/_custom';
import Tooltip from '../../../../common/_custom/Tooltip/Tooltip';
import SideDrawer from '../../../../common/_custom/SideDrawer/SideDrawer';
import SideDrawerFooter from '../../../../common/_custom/SideDrawer/components/SideDrawerFooter';
import BottomDrawer from '../../../../common/_custom/BottomDrawer/BottomDrawer';
import { useIsMobile } from '../../../../common/_custom/Hooks/useIsMobile';

import greyLock from '../../../../assets/grey-lock-2.svg';
import { lockOpenRight } from '../../../../assets/hostedassets';
import styles from './RefreshReport.module.scss';
import { fetchCompanyOverviewData } from '../../../../store/dashboard/overview/actions';
import { updateQueryParams } from '../../../../utils/searchParamUtils';
import { getSubdomain } from '../../../../utils/utils';
import { AICA_UNLOCK_REFRESH_UPDATE } from '../../../../utils/constants/mixpanelEvents/investorEvents';
import { Mixpanel } from '../../../../utils/mixpanel';
import LoadingSpinner from '../../../../common/_custom/LoadingSpinner/LoadingSpinner';
import { useMcaPolling } from '../../../../utils/customHooks';
import { OverviewData } from '../../../../store/dashboard/overview/types';

dayjs.extend(relativeTime);

// The shared CheckBox ships with `padding: 0.75rem 1rem` baked in, which makes its real
// footprint ~56px — that's exactly why the desktop table's first column is 56px wide.
// Figma's mobile layout (node 3741-66766) uses a 45px column with the box 16px from the
// table edge, so the built-in padding is reset and the grid cell owns the offset instead.
const CHECKBOX_RESET_STYLE: React.CSSProperties = { padding: 0 };

type Props = {
    close: () => void;
    onUnlockReportSuccess: () => void;
    selectedCompany: any;
};

const RefreshReport: React.FC<Props> = ({ close, onUnlockReportSuccess, selectedCompany }) => {
    const dispatch = useAppDispatch();
    const params = useParams();
    // Mobile-only: renders as a bottom sheet instead of the desktop SideDrawer — RECUR-27903.
    const isMobile = useIsMobile();

    const [overviewData, setOverviewData] = useState<OverviewData | null>(null);

    const orgId: string = params.id as string;
    const user = useAppSelector((state) => state.user);
    const dashboardConfig: any = useAppSelector((state) => state.investor.config);
    const permissions = useAppSelector((state) => state.investor.permissions);
    const tabLockStatus = useAppSelector((state) => state.investor.tabLockStatus);
    const taskTrackerMap = useAppSelector((state) => state.investor.taskTrackerMap);
    const modulePollingList = useAppSelector((state) => state.investor.modulePollingList);
    const extendedPollingModules = useAppSelector((state) => (state.investor as any).extendedPollingModules ?? []);
    const effectivePollingList = useMemo(
        () => Array.from(new Set([...(modulePollingList || []), ...(extendedPollingModules as string[])])),
        [modulePollingList, extendedPollingModules],
    );
    const investorData = useAppSelector((state) => state.investor);
    const selectedCompanyFromRedux = useAppSelector((state) => state.investor.selectedCompany);
    const profileGenerationStatus = investorData?.selectedCompany?.profileGenerationStatus;

    const { mcaDocData } = useMcaPolling(orgId);

    const onboardingTabs = permissions?.onboardingTabs;
    const isProfileLocked = profileGenerationStatus === 'LOCKED';
    const allRefreshableModules = dashboardConfig?.tabs?.filter((tab: any) => tab.refreshableModule === true);
    const primaryModuleRefreshList =
        dashboardConfig?.tabs?.filter((tab: any) => tab.refreshableModule === true && tab.category === 'primary') || [];
    // Resolved signal + sticky CROSS_MODULE_TASK_STEPS cache, published by TabSelector (the sole
    // fetcher) — see store/investor/taskTrackerResolution.ts. Reading the shared copy instead of
    // maintaining a second one here is what keeps this drawer's Management Profile row agreeing
    // with the sidebar.
    const { resolved, cache } = useTaskTrackerResolution();
    const [unlocking, setUnlocking] = useState(false);
    // This drawer mounts fresh on every open ({showRefreshReport && <RefreshReport/>} in
    // CompanyDashboardParent), so a snapshot-on-mount initializer is enough — no effect needed
    // to keep it in sync. Consumed once, then cleared below so every OTHER entry point
    // (TabSelector, HeaderButtonContainer) keeps opening with nothing preselected.
    const preselectedModules = useAppSelector((state) => state.investor.refreshReportPreselectedModules);
    const [selectedModules, setSelectedModules] = useState<Set<string>>(() => new Set(preselectedModules));
    const [searchParams, setSearchParams] = useSearchParams();
    const location = useLocation();

    useEffect(() => {
        dispatch(setRefreshReportPreselectedModules([]));
    }, [dispatch]);

    // Get overallStatus from Redux state instead of making API call
    const overallStatus = selectedCompanyFromRedux?.overallStatus || '';

    /**
     * Determines if a tab is locked based on multiple factors:
     * - If overview tab and overallStatus is LOCKED, always lock the overview tab
     * - For primary modules, trust the tabLockStatus from Polus
     * - For secondary modules, check task tracker evidence to determine if unlocked; if no evidence, treat as locked
     */
    const isTabLocked = useCallback(
        (tabId: string) => {
            // Priority check: If overview tab and overallStatus is LOCKED, always lock the overview tab
            if (tabId === 'overview' && overallStatus === 'LOCKED') {
                return true;
            }

            // Primary modules (Banking etc.) — trust polus lock status, default to locked if no data yet
            if (primaryModuleRefreshList.some((tab: any) => tab.id === tabId)) {
                const polusEntry = tabLockStatus?.[tabId];
                if (polusEntry == null) return TAB_LOCK_IDS_AFTER_DIRECTORS_OVERVIEW.includes(tabId);
                return !!polusEntry.isLocked;
            }

            if (tabId === TabModule.MANAGEMENT_PROFILE) {
                // Shared with TabSelector's sidebar via the same pure predicate — see
                // store/investor/taskTrackerResolution.ts — so the two surfaces can't diverge.
                return isManagementProfileLocked(resolved, taskTrackerMap, cache, tabLockStatus);
            }

            const steps = TAB_TO_TASK_TRACKER_MAP[tabId] || [];
            const isTerminalStatus = (s?: string) => s === 'COMPLETE' || s === 'FAILED' || s === 'FAILURE';
            let anyStepCompleteOrFailed = false;
            if (!isEmpty(taskTrackerMap)) {
                const taskTrackerSteps = steps
                    .map((step) => {
                        const cachedStatus = cache[step]?.status;
                        if (CROSS_MODULE_TASK_STEPS.has(step) && isTerminalStatus(cachedStatus)) return cachedStatus;
                        if (step in taskTrackerMap) return taskTrackerMap[step].status;
                        return cachedStatus ?? null;
                    })
                    .filter((status): status is string => status !== null);
                anyStepCompleteOrFailed = !isEmpty(taskTrackerSteps) && taskTrackerSteps.some(isTerminalStatus);
            }

            if (anyStepCompleteOrFailed) return false;

            const isRefreshTransient = isEmpty(taskTrackerMap) && modulePollingList.length > 0;
            if (isRefreshTransient) return !!tabLockStatus?.[tabId]?.isLocked;

            return true;
        },
        [isProfileLocked, onboardingTabs, taskTrackerMap, tabLockStatus, overallStatus, modulePollingList, resolved, cache],
    );

    useEffect(() => {
        dispatch(
            fetchCompanyOverviewData({
                assesseeOrgId: orgId,
                investorOrgId: user.domainMetadata?.investorOrgId ?? '',
                onSuccess: (data: OverviewData, actionCta) => {
                    setOverviewData(data);
                },
            }),
        );
    }, [orgId, user.domainMetadata?.investorOrgId]);

    const handleModuleSelection = (module: string) => {
        const newSelectedModules = new Set<string>(selectedModules);

        if (module === 'all') {
            if (selectedModules.size === allRefreshableModules.length) {
                newSelectedModules.clear();
            } else {
                allRefreshableModules.forEach((module) => newSelectedModules.add(module.id));
            }
        } else if (selectedModules.has(module)) {
            newSelectedModules.delete(module);
        } else {
            newSelectedModules.add(module);
        }

        setSelectedModules(newSelectedModules);
    };

    const onPrimary = () => {
        Mixpanel.track(AICA_UNLOCK_REFRESH_UPDATE, {
            userRole: user?.userData?.accountType,
            pnoPlatform: getSubdomain(),
            userId: user?.userData?.userId,
            currentUrl: window.location.href,
            organizationId: orgId,
            modulesForUnlock: Array.from(selectedModules).filter((module) => isTabLocked(module)),
            modulesForRefresh: Array.from(selectedModules).filter((module) => !isTabLocked(module)),
            modulesCountForUpdate: selectedModules.size,
        });

        setUnlocking(true);

        unlockOrRefreshReport(
            {
                assesseeOrgId: orgId,
                investorOrgId: user.domainMetadata?.investorOrgId ?? '',
                tabs: Array.from(selectedModules),
            },
            {
                onSuccess: () => {
                    setUnlocking(false);
                    const affectedModules = new Set<string>(selectedModules);
                    selectedModules.forEach((id) => {
                        const tab = dashboardConfig?.tabs?.find((t: any) => t.id === id);
                        const subModuleIds = (tab?.subModules || [])
                            .map((name: string) => dashboardConfig?.tabs?.find((t: any) => t.label === name)?.id)
                            .filter(Boolean);
                        subModuleIds.forEach((t: string) => affectedModules.add(t));
                    });
                    dispatch(setTaskTrackerMap({}));
                    dispatch(setModulePollingList(Array.from(affectedModules)));
                    if (profileGenerationStatus === 'LOCKED') {
                        onUnlockReportSuccess();
                    }
                    dispatch(
                        fetchCompanyOverviewData({
                            assesseeOrgId: orgId,
                            investorOrgId: user.domainMetadata?.investorOrgId ?? '',
                            onSuccess: () => {
                                updateQueryParams(searchParams, setSearchParams, { triggerMain: true }, location);
                            },
                            onError: () => {},
                        }),
                    );
                    close();
                },
                onError: () => {
                    setUnlocking(false);
                },
            },
        ).finally(() => {
            setUnlocking(false);
        });
    };

    /* 
        Filters out cross-module task steps from each module's step list to compute exclusive steps.
        Used for last_fetched date — prevents one module's refresh from updating another module's date.
    */
    const exclusiveStepsMap = useMemo(() => {
        const result: Record<string, string[]> = {};
        const refreshableIds = (allRefreshableModules || []).map((m: any) => m.id);
        refreshableIds.forEach((id: string) => {
            result[id] = (TAB_TO_TASK_TRACKER_MAP[id] || []).filter((step) => !CROSS_MODULE_TASK_STEPS.has(step));
        });
        return result;
    }, [allRefreshableModules]);

    // Shared per-module derivation used by BOTH the desktop table and the mobile card list below,
    // so the two views can never drift on status/date logic — RECUR-27903.
    const moduleRows = useMemo(() => {
        return allRefreshableModules.map((module) => {
            let lastFetchedInWords = '-';
            const moduleTasks = TAB_TO_TASK_TRACKER_MAP[module.id] || [];
            const exclusiveTasks = exclusiveStepsMap[module.id] || [];
            const locked = isTabLocked(module.id);
            // Same fetching state as TabSelector's sidebar spinner — shares
            // isManagementProfileFetchInFlight so the two surfaces can't disagree.
            // While unresolved, isManagementProfileLocked reads false too, so this
            // also covers "not known to be locked" ≠ "known to be unlocked" the same
            // way the sidebar does, instead of showing a premature "Unlocked".
            const isFetchingManagementProfile =
                module.id === TabModule.MANAGEMENT_PROFILE && (!resolved || isManagementProfileFetchInFlight(taskTrackerMap, cache));

            // Only show last fetched if the module itself is unlocked.
            // Prefer exclusive steps to prevent cross-module date leaks.
            if (!locked && taskTrackerMap && moduleTasks.length) {
                const exclusiveFetched = exclusiveTasks
                    .filter((task) => taskTrackerMap[task]?.status === 'COMPLETE')
                    .map((task) => taskTrackerMap[task]?.dateCreated)
                    .filter(Boolean)
                    .sort((a, b) => b - a)[0];
                const lastFetched =
                    exclusiveFetched ||
                    moduleTasks
                        .filter((task) => taskTrackerMap[task]?.status === 'COMPLETE')
                        .map((task) => taskTrackerMap[task]?.dateCreated)
                        .filter(Boolean)
                        .sort((a, b) => b - a)[0];
                lastFetchedInWords = lastFetched ? dayjs(lastFetched).fromNow() : '-';
            }

            const isFinancialsModule = module.id === 'auditedFinancials';
            const isProcessing = isFinancialsModule && mcaDocData?.reportStatus === 'Processing' && mcaDocData?.isFetching === true;

            const subModules = module?.subModules;
            const moreCount = subModules?.length > 1 ? subModules.length - 1 : 0;
            // Prefer an explicit display label from config when present (e.g. "Company Details & GST Overview"),
            // otherwise fall back to summarising the functional subModules list.
            const subModulesText = module?.subModulesLabel ? (
                <>({module.subModulesLabel})</>
            ) : subModules?.length ? (
                <>
                    ({subModules[0]} {moreCount > 0 && <span className={styles.SubModulesMore}> +{moreCount} more</span>})
                </>
            ) : null;
            const isSelected = selectedModules.has(module.id);
            const isFetching = effectivePollingList.includes(module?.id as string) || isFetchingManagementProfile;

            return { module, locked, isProcessing, subModules, subModulesText, isSelected, isFetching, lastFetchedInWords };
        });
    }, [
        allRefreshableModules,
        selectedModules,
        taskTrackerMap,
        mcaDocData,
        effectivePollingList,
        exclusiveStepsMap,
        isTabLocked,
        resolved,
        cache,
    ]);

    const renderModuleSelectionTable = useMemo(() => {
        return (
            <table className={styles.ModuleSelectionTable}>
                <thead className={styles.ModuleSelectionTableHead}>
                    <tr>
                        <th style={{ width: '56px' }}>
                            {/* <CheckBox
                                checked={selectedModules.size === allRefreshableModules.length}
                                label=""
                                onCheck={() => handleModuleSelection('all')}
                                variant="primary"
                            /> */}
                        </th>
                        <th style={{ width: '19.188rem' }}>List of modules</th>
                        <th>Status</th>
                        <th>Last fetched</th>
                    </tr>
                </thead>

                <tbody className={styles.ModuleSelectionTableBody}>
                    {moduleRows.map(
                        ({ module, locked, isProcessing, subModules, subModulesText, isSelected, isFetching, lastFetchedInWords }) => (
                            <tr key={module.id} className={isSelected ? styles.SelectedRow : ''}>
                                <td>
                                    <CheckBox
                                        checked={isSelected}
                                        label=""
                                        onCheck={() => handleModuleSelection(module.id)}
                                        variant="primary"
                                    />
                                </td>
                                <td>
                                    <div className={styles.ModuleName}>
                                        <div className={styles.ModuleLabel}>
                                            <span>{module.label}</span>
                                            {subModulesText &&
                                                (!module?.subModulesLabel && subModules?.length > 1 ? (
                                                    <Tooltip
                                                        content={subModules.join(', ')}
                                                        direction="top"
                                                        usePortal={true}
                                                        fitContent
                                                        style={{ maxWidth: '15rem', maxHeight: '3.5rem' }}
                                                    >
                                                        <span className={styles.SubModulesSummary}>{subModulesText}</span>
                                                    </Tooltip>
                                                ) : (
                                                    <span className={styles.SubModulesSummary}>{subModulesText}</span>
                                                ))}
                                        </div>
                                        {isProcessing && <LoadingSpinner color="#6021b3" height="15px" className={styles.InlineSpinner} />}
                                    </div>
                                </td>
                                <td>
                                    {isFetching ? (
                                        <LoadingSpinner color="#6021b3" height="15px" />
                                    ) : locked ? (
                                        <div className={styles.StatusLocked}>
                                            <span>Locked</span>
                                            <img src={greyLock} alt="" width="14" height="14" />
                                        </div>
                                    ) : (
                                        <div className={styles.StatusUnlocked}>
                                            <span>Unlocked</span>
                                            <img src={lockOpenRight} alt="" width="12" height="12" />
                                        </div>
                                    )}
                                </td>
                                <td className={styles.LastFetched}>
                                    {isProcessing ? (
                                        <span className={styles.FetchingText}>Fetching latest data from MCA (takes upto 24 hours)</span>
                                    ) : isFetching ? (
                                        <span className={styles.FetchingText}>Fetching…</span>
                                    ) : (
                                        lastFetchedInWords
                                    )}
                                </td>
                            </tr>
                        ),
                    )}
                </tbody>
            </table>
        );
    }, [moduleRows]);

    // Mobile-only list (Figma node 3741-66766). The header and every data row render through
    // the SAME grid class (.MobileModuleGridRow), so the checkbox / name / last-fetched columns
    // are structurally incapable of drifting out of alignment. Desktop's
    // renderModuleSelectionTable above is untouched. RECUR-27903.
    const renderMobileModuleList = useMemo(() => {
        // Mobile currently supports only these two modules — same scoping as the mobile
        // sidebar's TabSelector. Desktop still lists every refreshable module.
        const mobileRows = moduleRows.filter(({ module }) => [TabModule.OVERVIEW, TabModule.MANAGEMENT_PROFILE].includes(module.id));

        return (
            <div className={styles.MobileModuleList}>
                <div className={clsx(styles.MobileModuleGridRow, styles.MobileModuleHeadRow)}>
                    {/* No select-all control — the empty first cell still occupies the grid's
                        fixed checkbox column, so the header stays aligned with the rows. */}
                    <span />
                    <span>List of modules</span>
                    <span className={styles.MobileFetchedCell}>Last fetched</span>
                </div>
                {mobileRows.map(({ module, locked, isProcessing, subModulesText, isSelected, isFetching, lastFetchedInWords }) => (
                    <div
                        key={module.id}
                        className={clsx(styles.MobileModuleGridRow, { [styles.SelectedRow]: isSelected })}
                        onClick={() => handleModuleSelection(module.id)}
                    >
                        {/* Stops the click from also bubbling to the row's onClick below — the
                            unstyled CheckBox doesn't stopPropagation itself, so without this a tap
                            directly on the checkbox ran both handlers for the same interaction. */}
                        <span className={styles.MobileCheckCell} onClick={(e) => e.stopPropagation()}>
                            <CheckBox
                                checked={isSelected}
                                label=""
                                onCheck={() => handleModuleSelection(module.id)}
                                variant="primary"
                                style={CHECKBOX_RESET_STYLE}
                            />
                        </span>
                        <span className={styles.MobileModuleName}>
                            <span className={styles.MobileModuleLabel}>{module.label}</span>
                            {subModulesText && <span className={styles.SubModulesSummary}>{subModulesText}</span>}
                            {locked && <img src={greyLock} alt="Locked" width="16" height="16" />}
                            {isProcessing && <LoadingSpinner color="#6021b3" height="15px" className={styles.InlineSpinner} />}
                        </span>
                        <span className={clsx(styles.MobileFetchedCell, styles.MobileModuleFetched)}>
                            {isProcessing || isFetching ? 'Fetching…' : lastFetchedInWords}
                        </span>
                    </div>
                ))}
            </div>
        );
    }, [moduleRows]);

    if (isMobile) {
        // Bottom sheet instead of the desktop side drawer — same pattern as CommentsThread's mobile
        // branch (BottomDrawer, safe-area-aware padding). Desktop's SideDrawer below is untouched. RECUR-27903.
        return (
            <BottomDrawer
                dontClose
                onClose={() => close()}
                height="85%"
                showHeading
                heading={<div className={styles.DrawerHeader}>Unlock/Refresh Report</div>}
                style={{ paddingBottom: 'calc(0.5rem + env(safe-area-inset-bottom))' }}
            >
                {/* No inline height here (unlike desktop): this is the flex child that scrolls,
                    so the drawer's sticky header and the footer below stay put. RECUR-27903. */}
                <div className={clsx(styles.RefreshReportContainer, styles.MobileRefreshReportContainer)}>
                    <div className={clsx(styles.RefreshReportContent, styles.MobileRefreshReportContent)}>
                        {renderMobileModuleList}
                        <p className={styles.RefrestNote}>
                            User consent-based data from GST, Debt Schedule etc. will be updated automatically when available.
                        </p>
                    </div>
                </div>
                {/* Equal-width CTAs + safe-area padding, matching CommentsThread's mobile .Buttons footer. */}
                <div className={styles.MobileFooterButtons}>
                    <Button type="submit" onClick={close} variant="secondary" text="Cancel" />
                    <Button
                        type="submit"
                        disabled={selectedModules.size === 0}
                        loading={unlocking}
                        onClick={onPrimary}
                        variant="primary"
                        text="Update"
                    />
                </div>
            </BottomDrawer>
        );
    }

    return (
        <SideDrawer
            showHeading
            heading={<div className={styles.DrawerHeader}>Unlock/Refresh Report</div>}
            onClose={() => close()}
            width="44.438rem"
        >
            <div className={styles.RefreshReportContainer} style={{ height: '100%' }}>
                <div className={styles.RefreshReportContent}>
                    {renderModuleSelectionTable}
                    <p className={styles.RefrestNote}>
                        User consent-based data from GST, Debt Schedule etc. will be updated automatically when available.
                    </p>
                </div>

                <SideDrawerFooter>
                    <div className={styles.FooterContainer}>
                        <Button
                            style={{ marginTop: 0 }}
                            type="submit"
                            disabled={selectedModules.size === 0}
                            loading={unlocking}
                            onClick={onPrimary}
                            variant="primary"
                            text="Update"
                        />
                        <Button style={{ marginTop: 0 }} type="submit" onClick={close} variant="secondary" text="Cancel" />
                    </div>
                </SideDrawerFooter>
            </div>
        </SideDrawer>
    );
};

export default RefreshReport;
